import { cn } from "@/lib/utils";

/**
 * Accessible on/off switch - a real toggle instead of a boolean
 * select. Used for Location status, can_manage_sub_partners, enabled
 * flags, etc. Keyboard-operable (Space / Enter) via native button
 * semantics.
 *
 * Size variants:
 *   - "sm" (36×20) for compact table rows
 *   - "md" (44×24) default for forms
 */
export interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  size?: "sm" | "md";
  /** Short text rendered to the right of the track. */
  label?: string;
  /** Dimmer text that describes the effect of the on vs off state. */
  description?: string;
  /** Labels for screen readers when no visible label fits. */
  ariaLabel?: string;
  /**
   * Optional per-state labels inside the track, e.g. "ON / OFF" or
   * "Active / Inactive". Hidden on `sm` for space.
   */
  onLabel?: string;
  offLabel?: string;
  id?: string;
  className?: string;
}

export function Toggle(props: ToggleProps): JSX.Element {
  const {
    checked,
    onChange,
    disabled,
    size = "md",
    label,
    description,
    ariaLabel,
    onLabel,
    offLabel,
    id,
    className,
  } = props;
  const track = size === "sm" ? "h-5 w-9" : "h-6 w-11";
  const knob =
    size === "sm"
      ? checked
        ? "h-4 w-4 translate-x-4"
        : "h-4 w-4 translate-x-0.5"
      : checked
        ? "h-5 w-5 translate-x-5"
        : "h-5 w-5 translate-x-0.5";

  return (
    <div className={cn("inline-flex items-center gap-2", className)}>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-label={ariaLabel ?? label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex flex-shrink-0 cursor-pointer items-center rounded-full border transition-colors",
          track,
          checked ? "border-primary bg-primary" : "border-muted-foreground/30 bg-muted",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none inline-block transform rounded-full bg-white shadow transition-transform",
            knob,
          )}
        />
        {size === "md" && (onLabel || offLabel) && (
          <span
            aria-hidden="true"
            className={cn(
              "absolute text-[9px] font-semibold uppercase tracking-wide",
              checked ? "left-1.5 text-primary-foreground" : "right-1.5 text-muted-foreground",
            )}
          >
            {checked ? onLabel : offLabel}
          </span>
        )}
      </button>
      {(label || description) && (
        <label htmlFor={id} className="flex cursor-pointer flex-col">
          {label && <span className="text-sm font-medium">{label}</span>}
          {description && <span className="text-[11px] text-muted-foreground">{description}</span>}
        </label>
      )}
    </div>
  );
}
